import React from "react";
import { Link } from "react-router-dom";
import WebLayout from "../components/WebLayout";
import Seo from "../components/Seo";
import {
  ArrowRight, Building2, Warehouse, Factory, Home, Truck, Dumbbell, Heart, Store,
  ClipboardList, ShieldCheck, Handshake, MapPin, Zap,
  Search, Wallet, ShoppingBasket,
  MessageCircle, Layers,
} from "lucide-react";

// ─── Premium dark-cinematic imagery (nano-banana-generated) ────────
const IMG = {
  hero:         "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/2e5dc81e55d5680ea44dfd714dc28fc12f44765e72eae9bbfb98094cb986967f.jpeg",
  get_matched:  "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/e95cd7818c071daeee3ec58b1ecc7eca8364006198a0e5d998c6af0c10be0d25.jpeg",
  scout:        "/scout-warehouse.jpg",
  operator:     "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/23e0574383f7b487cb98b6402beadd333fea5e379dba83e81b031656611aa735.jpeg",
  vend_snack:   "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/35f8ece88fffebfb55601a515cf8f0dada0f63fabe223e233e199cb683b8c24e.jpeg",
  vend_drink:   "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/62f18d4a767f18397071feb718ce898ce7f205bf116b52f07434083baef6a943.jpeg",
  vend_combo:   "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/90fd242a52a0ce5bddb4f86a17fff3b607e9f337847ca1c8a0e19f8ed43019c8.jpeg",
  vend_coffee:  "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/bf800be55ded6cc6453ef82d049486de99fa3401d8efb4e35287a3ee9f2d056c.jpeg",
  vend_fresh:   "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/e93ad616fcf5dab7e455ba5587d37b1f1ceac9b97178c00e02feb83061de0605.jpeg",
  vend_micro:   "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/fed566b259cd1b178811e7facce5bb64c3264f0fb49e9e39df91dc6d769064d0.jpeg",
  loc_warehouse:"https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/6b69129d842f8a588e27355b3a9ca37946dd1b6ccded1dcc4d0ac817a9b77b61.jpeg",
  loc_mfg:      "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/33acca9e9857b98a0f60086e79b56ff5ecfdc89ac5f3d4598ac74007c381487b.jpeg",
  loc_office:   "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/8414c92d69b268cb845df0d77f34112e722480621a21437aa61cb63c67541d34.jpeg",
  loc_apts:     "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/b8bc4fe94a7b7a91e9e88573b0e2fd592c2ad4971aee1366300269447458b641.jpeg",
  loc_truck:    "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/2d89c5a2ab9a957ccc4c5a419c7f61754187dc58ec8bc9ebdd36d24d11a88dbe.jpeg",
  loc_gym:      "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/a8536bddc391cd620347e558a6b04492be8cc742121487cdd127e41f8b568675.jpeg",
  loc_senior:   "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/f4a11e55708c74467e550575b97b67e159a69da97138384cc4602b7d291102c9.jpeg",
  loc_dealer:   "https://static.prod-images.emergentagent.com/jobs/98b8b772-a971-43ed-ab79-2392fa47e3a1/images/6b2aad93c82bb920669165f9a64398cbdcafb1f13494d3c7e22283625b221474.jpeg",
};

// ─── HERO ─────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-[#2A2F34]">
      {/* Hero background — brighter v3 image; softer overlay lets vending machines stay visible while headline column remains crisp */}
      <div className="absolute inset-0" aria-hidden>
        <img
          src={IMG.hero}
          alt=""
          width={2000}
          height={1125}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
        {/* Left→right dark fade — solid over headline, transparent over machines */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#080B0D] via-[#080B0D]/70 to-transparent" />
        {/* Bottom transition into next section */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#080B0D]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 sm:pt-24 sm:pb-28 lg:pt-32 lg:pb-36">
        <div className="max-w-3xl">
          <div className="sb-eyebrow text-[#FFC107]">For Businesses</div>
          <h1 className="mt-4 text-[38px] leading-[1.02] sm:text-[52px] lg:text-[72px] font-black tracking-tight">
            NEED VENDING AT<br /><span className="text-[#FFC107]">YOUR WORKPLACE?</span>
          </h1>
          <p className="mt-6 text-[15px] sm:text-[17px] text-[#A1A1AA] leading-relaxed max-w-2xl">
            SpotBounty helps businesses connect with independent vending operators
            for snacks, drinks, food, coffee, and other vending solutions.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/businesses" data-testid="hero-get-vending" className="sb-btn-primary text-[13px]" style={{ padding: "16px 26px" }}>
              Get Vending <ArrowRight size={16} />
            </Link>
            <Link to="/scouts" data-testid="hero-become-scout" className="sb-btn-secondary text-[13px]" style={{ padding: "16px 26px" }}>
              Become a Scout
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── BENEFITS ─────────────────────────────────────────────────────
function Benefits() {
  const items = [
    { icon: Wallet,      title: "No equipment purchase required",
      body: "For qualifying placements with participating operators." },
    { icon: ShoppingBasket, title: "Multiple vending formats",
      body: "Snacks, drinks, coffee, fresh food, and micro markets." },
    { icon: ClipboardList,  title: "Simple setup process",
      body: "Tell us about your location — we’ll do the coordination." },
    { icon: Handshake,      title: "Independent, local operators",
      body: "Connect with operators servicing your area." },
  ];
  return (
    <section className="border-b border-[#2A2F34]">
      <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {items.map(({ icon: Icon, title, body }, i) => (
          <div key={i} className="sb-card p-6" data-testid={`benefit-${i}`}>
            <div className="w-11 h-11 rounded-lg bg-[#FFC107]/10 border border-[#FFC107]/30 flex items-center justify-center text-[#FFC107]">
              <Icon size={20} />
            </div>
            <div className="mt-4 text-[15px] font-bold leading-snug">{title}</div>
            <div className="mt-1.5 text-[13px] text-[#A1A1AA] leading-relaxed">{body}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── HOW IT WORKS ─────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    { n: "01", icon: MapPin,       title: "Tell us about your location",
      body: "Share basic details: workplace type, approximate people, and what vending options interest you." },
    { n: "02", icon: ShieldCheck,  title: "We verify what you need",
      body: "Our team confirms the fit and prepares an information package for local operators." },
    { n: "03", icon: Handshake,    title: "We connect you with an operator",
      body: "You review the introduction and coordinate directly with an independent vending operator." },
  ];
  return (
    <section id="how" className="border-b border-[#2A2F34] bg-[#0B0E11]">
      <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
        <div className="max-w-2xl">
          <div className="sb-eyebrow">How it works</div>
          <h2 className="mt-3 text-[28px] sm:text-[36px] font-black leading-tight">Three steps to workplace vending.</h2>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {steps.map(({ n, icon: Icon, title, body }, i) => (
            <div key={i} className="sb-card p-6 relative" data-testid={`how-step-${i}`}>
              <div className="text-[11px] tracking-[.3em] text-[#52525b] font-mono">{n}</div>
              <div className="mt-3 w-10 h-10 rounded-lg bg-[#FFC107]/10 border border-[#FFC107]/30 flex items-center justify-center text-[#FFC107]">
                <Icon size={18} />
              </div>
              <div className="mt-3 text-[16px] font-bold leading-snug">{title}</div>
              <div className="mt-1.5 text-[13px] text-[#A1A1AA] leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-xl border border-[#2A2F34] bg-[#15191D] p-4 text-[12.5px] text-[#A1A1AA] leading-relaxed max-w-3xl">
          SpotBounty does not own or operate vending machines. We connect businesses
          with independent vending operators.
        </div>
      </div>
    </section>
  );
}

// ─── VENDING OPTIONS ──────────────────────────────────────────────
function VendingOptions() {
  const items = [
    { img: IMG.vend_snack,  label: "Snack" },
    { img: IMG.vend_drink,  label: "Drink" },
    { img: IMG.vend_combo,  label: "Combo" },
    { img: IMG.vend_coffee, label: "Coffee & beverage" },
    { img: IMG.vend_fresh,  label: "Fresh food / smart cooler" },
    { img: IMG.vend_micro,  label: "Micro markets" },
  ];
  return (
    <section className="border-b border-[#2A2F34]">
      <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
        <div className="sb-eyebrow">Vending options</div>
        <h2 className="mt-3 text-[28px] sm:text-[36px] font-black leading-tight">Match the format to your workplace.</h2>
        <div className="mt-10 grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
          {items.map(({ img, label }, i) => (
            <div key={i} className="sb-card overflow-hidden hover:border-[#FFC107]/40 transition-colors" data-testid={`vending-${i}`}>
              <div className="relative aspect-[4/3] bg-[#0B0E11]">
                <img
                  src={img}
                  alt={label}
                  width={400}
                  height={300}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#15191D] to-transparent" />
              </div>
              <div className="px-4 py-3.5 text-[13px] font-bold text-center">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── LOCATION TYPES ───────────────────────────────────────────────
function LocationTypes() {
  const items = [
    { icon: Warehouse, label: "Warehouses & distribution", img: IMG.loc_warehouse },
    { icon: Factory,   label: "Manufacturing",              img: IMG.loc_mfg },
    { icon: Building2, label: "Offices",                    img: IMG.loc_office },
    { icon: Home,      label: "Apartments",                 img: IMG.loc_apts },
    { icon: Truck,     label: "Truck & auto facilities",    img: IMG.loc_truck },
    { icon: Dumbbell,  label: "Gyms",                       img: IMG.loc_gym },
    { icon: Heart,     label: "Senior living",              img: IMG.loc_senior },
    { icon: Store,     label: "Dealerships & other",        img: IMG.loc_dealer },
  ];
  return (
    <section className="border-b border-[#2A2F34] bg-[#0B0E11]">
      <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
        <div className="sb-eyebrow">Location types</div>
        <h2 className="mt-3 text-[28px] sm:text-[36px] font-black leading-tight">Vending built for your kind of workplace.</h2>
        <div className="mt-10 grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {items.map(({ icon: Icon, label, img }, i) => (
            <Link to="/businesses" key={i} className="sb-card overflow-hidden group hover:border-[#FFC107]/40 transition-colors block" data-testid={`vertical-${i}`}>
              <div className="relative h-[130px] bg-[#15191D]">
                <img
                  src={img}
                  alt={label}
                  width={400}
                  height={260}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#15191D] via-[#15191D]/25 to-transparent" />
              </div>
              <div className="p-5">
                <div className="relative -mt-9 w-9 h-9 rounded-lg bg-[#15191D] border border-[#2A2F34] flex items-center justify-center text-[#FFC107] group-hover:border-[#FFC107]/40 transition-colors">
                  <Icon size={18} />
                </div>
                <div className="mt-3 text-[14px] font-bold leading-snug">{label}</div>
                <div className="mt-1 text-[12px] text-[#52525b] flex items-center gap-1 group-hover:text-[#FFC107] transition-colors">
                  Request vending <ArrowRight size={12} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BUSINESS REQUEST CTA ─────────────────────────────────────────
function BusinessRequestCTA() {
  return (
    <section className="border-b border-[#2A2F34] relative overflow-hidden">
      <div className="absolute inset-0 opacity-20" aria-hidden>
        <img src={IMG.get_matched} alt="" width={2000} height={1200} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080B0D] via-[#080B0D]/95 to-[#080B0D]/70" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 py-16 sm:py-20 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
        <div>
          <div className="sb-eyebrow text-[#FFC107]">Tell us about your location</div>
          <h2 className="mt-3 text-[30px] sm:text-[42px] font-black leading-[1.05]">
            Get matched with an <span className="text-[#FFC107]">independent vending operator</span> in your area.
          </h2>
          <p className="mt-4 text-[14.5px] text-[#A1A1AA] leading-relaxed max-w-xl">
            Share a few details about your workplace and what vending options you&apos;re
            interested in. We&apos;ll verify the fit and introduce you to an independent
            operator servicing your market.
          </p>
          <div className="mt-6">
            <Link to="/businesses" data-testid="cta-request-form" className="sb-btn-primary text-[13px]" style={{ padding: "16px 28px" }}>
              Request Vending Service <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="sb-card p-6">
          <div className="text-[11px] tracking-widest text-[#52525b] uppercase">What we’ll ask</div>
          <ul className="mt-4 space-y-3 text-[13.5px] text-[#A1A1AA]">
            {[
              "Business type (warehouse, office, apartment, etc.)",
              "Approximate people or traffic",
              "Vending formats you’re interested in",
              "Contact name, email, and phone",
            ].map((t, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#FFC107] shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 pt-5 border-t border-[#2A2F34] text-[12px] text-[#52525b]">
            SpotBounty is a marketplace. We do not install or operate vending machines directly.
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── WHY SPOTBOUNTY ───────────────────────────────────────────────
function WhySpotBounty() {
  const items = [
    { icon: MessageCircle, title: "Local communication",
      body: "You talk to operators who service your market, not a national call center." },
    { icon: MapPin,        title: "Operators service their own markets",
      body: "Independent operators with roots in your area handle setup and restocking." },
    { icon: Layers,        title: "Flexible solutions",
      body: "Snack, drink, combo, coffee, fresh food — match what fits your workplace." },
    { icon: Building2,     title: "Location-specific needs",
      body: "We ask about your shifts, traffic and space before making an introduction." },
  ];
  return (
    <section className="border-b border-[#2A2F34]">
      <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
        <div className="sb-eyebrow">Why SpotBounty</div>
        <h2 className="mt-3 text-[28px] sm:text-[36px] font-black leading-tight max-w-2xl">Built around local, independent vending operators.</h2>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {items.map(({ icon: Icon, title, body }, i) => (
            <div key={i} className="border-l-2 border-[#FFC107] pl-5" data-testid={`why-${i}`}>
              <Icon size={30} strokeWidth={1.75} className="text-[#FFC107]" aria-hidden />
              <div className="mt-4 text-[15px] font-bold">{title}</div>
              <div className="mt-1.5 text-[13px] text-[#A1A1AA] leading-relaxed">{body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── OTHER WAYS TO PARTICIPATE ────────────────────────────────────
function OtherWays() {
  const items = [
    { to: "/scouts",   img: IMG.scout,    imgPos: "40% 50%",  icon: Search, eyebrow: "For Scouts", title: "Find vending opportunities.",
      body: "Scouts identify workplaces that could use vending and earn when a qualifying opportunity sells to an operator." },
    { to: "/operators",img: IMG.operator, imgPos: "center",   icon: Zap,    eyebrow: "For Operators", title: "Access opportunities in your service area.",
      body: "Independent vending operators browse verified opportunities in the markets you already serve." },
  ];
  return (
    <section className="border-b border-[#2A2F34] bg-[#0B0E11]">
      <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
        <div className="sb-eyebrow">Other ways to participate</div>
        <h2 className="mt-3 text-[28px] sm:text-[36px] font-black leading-tight">Not a business? There&apos;s a place for you too.</h2>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {items.map(({ to, img, imgPos, icon: Icon, eyebrow, title, body }, i) => (
            <Link to={to} key={i} className="sb-card overflow-hidden block group hover:border-[#FFC107]/40 transition-colors" data-testid={`other-way-${i}`}>
              <div className="relative h-[170px] bg-[#15191D]">
                <img
                  src={img}
                  alt={eyebrow}
                  width={800}
                  height={240}
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: imgPos }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Soft bottom-only fade into card body — photo stays clearly visible */}
                <div className="absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-t from-[#15191D] to-transparent" />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-lg bg-[#FFC107]/10 border border-[#FFC107]/30 flex items-center justify-center text-[#FFC107]">
                    <Icon size={20} />
                  </div>
                  <div className="text-[11px] tracking-widest text-[#A1A1AA] uppercase">{eyebrow}</div>
                </div>
                <div className="mt-5 text-[20px] font-black leading-tight">{title}</div>
                <div className="mt-2 text-[13px] text-[#A1A1AA] leading-relaxed">{body}</div>
                <div className="mt-5 text-[12.5px] text-[#FFC107] flex items-center gap-1.5">
                  Learn more <ArrowRight size={13} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FINAL CTA ────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 opacity-25" aria-hidden>
        <img src="https://images.unsplash.com/photo-1565034946487-077786996e27?w=2000&q=80&auto=format" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080B0D]/80 via-[#080B0D]/95 to-[#080B0D]" />
      </div>
      <div className="relative max-w-4xl mx-auto px-6 py-20 sm:py-28 text-center">
        <h2 className="text-[32px] sm:text-[48px] font-black leading-[1.05]">
          Ready to bring <span className="text-[#FFC107]">vending</span> to your workplace?
        </h2>
        <p className="mt-5 text-[15px] text-[#A1A1AA] max-w-2xl mx-auto">
          Tell us about your location and we’ll connect you with an independent vending operator in your area.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/businesses" data-testid="final-cta" className="sb-btn-primary text-[13px]" style={{ padding: "18px 32px" }}>
            Get Vending <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── PAGE ────────────────────────────────────────────────────────
export default function Landing() {
  return (
    <WebLayout>
      <Seo
        title="Need Vending at Your Workplace?"
        description="SpotBounty connects businesses with independent vending operators for snacks, drinks, food, and coffee."
        path="/"
        image="https://spotbounty.com/og-image.jpg"
        imageWidth={1200}
        imageHeight={630}
        imageType="image/jpeg"
        imageAlt="Need Vending at Your Workplace? SpotBounty connects businesses with independent vending operators."
      />
      <Hero />
      <Benefits />
      <HowItWorks />
      <VendingOptions />
      <LocationTypes />
      <BusinessRequestCTA />
      <WhySpotBounty />
      <OtherWays />
      <FinalCTA />
    </WebLayout>
  );
}
